import { useSyncExternalStore } from 'react'

/**
 * Verrou « le softphone est branché DANS CET ONGLET », tenu HORS de React.
 *
 * POURQUOI il existe : passer en « Se connecter en tant que » fait tomber l'éligibilité
 * (les rôles effectifs deviennent ceux du pharmacien, cf. `getRolesUserAndSwitchUser`, et
 * le profil REST est refetché sous son identité). Sans ce verrou, `canMountSoftphone`
 * passait de `true` à `false`, React rejouait le cleanup de l'effet de montage, et
 * `RingoverDialer.destroy()` retirait l'iframe du DOM — donc COUPAIT l'appel en cours.
 * Le verrou est le SEUL terme qui maintient l'iframe pendant une impersonation : tous les
 * autres (flag, rôle admin back-office, pays) sont faux à ce moment-là.
 *
 * Ce qu'il n'autorise PAS : appeler. `canCall` reste faux pendant l'impersonation — on ne
 * lance pas d'appel depuis le compte du pharmacien consulté.
 *
 * VOLONTAIREMENT EN MÉMOIRE (jamais sessionStorage) : un rechargement d'onglet coupe de
 * toute façon l'appel (l'iframe repart de zéro). Persister le verrou ne sauverait donc
 * rien et remonterait une iframe `allow="microphone"` sur un écran pharmacien sans que
 * rien ne l'explique — exactement le grief que la garde d'origine (commit f427d1c7d)
 * voulait écarter. Un onglet rechargé repart de zéro : pas de softphone tant que le
 * compte réel n'est pas de nouveau éligible.
 */
let armed = false

const listeners = new Set<() => void>()

const notify = (): void => listeners.forEach(listener => listener())

export const getSoftphoneLatch = (): boolean => armed

/** Snapshot serveur déterministe (anti-mismatch d'hydratation) : jamais armé en SSR. */
export const getSoftphoneLatchServerSnapshot = (): boolean => false

export const subscribeSoftphoneLatch = (listener: () => void): (() => void) => {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

/** Armé par `RingoverProvider` juste après `dialer.mount()` — jamais ailleurs. */
export const armSoftphone = (): void => {
  if (armed) return
  armed = true
  notify()
}

/** Désarmé au démontage réel du dialer et à la déconnexion (plus d'iframe à protéger). */
export const disarmSoftphone = (): void => {
  if (!armed) return
  armed = false
  notify()
}

/** Abonnement React — utilisable partout, y compris hors du provider. */
export const useSoftphoneLatch = (): boolean =>
  useSyncExternalStore(subscribeSoftphoneLatch, getSoftphoneLatch, getSoftphoneLatchServerSnapshot)
